import { useCentralStore } from "@/dentlab/src/store/Central";
import { useEffect } from "react";
import { Search, Loader2, HelpCircle, Sparkles } from "lucide-react";
import { PrivacySwitch } from "./header-bar-components/header-bar-settings-group/header-bar-settings-group-components/privacy-switch/privacy-switch.component";
import {
	SheetContentType,
	useSheetStore,
} from "@/dentlab/src/store/Sheets/sheet.store";
import { useHelpStore } from "@/lib/store/help.store";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { ReleaseNotesButton } from "@components/release-notes/release-notes-button.component";

export const HeaderBar: React.FC = () => {
	const { allDataInitialized } = useCentralStore((state) => ({
		allDataInitialized: state.allDataInitialized,
	}));
	const channelsLoading = useRealtimeStore((state) => state.channelsLoading);
	const { openSheet, sheets } = useSheetStore((state) => ({
		openSheet: state.openSheet,
		sheets: state.sheets,
	}));
	const openHelp = useHelpStore((state) => state.openHelp);
	const openAssistant = useHelpStore((state) => state.openAssistant);

	const handleOpenSearch = () => {
		if (
			sheets.length === 0 ||
			sheets[sheets.length - 1].sheetContentType !==
				SheetContentType.SEARCH
		) {
			openSheet(SheetContentType.SEARCH);
		}
	};

	const handleOpenHelp = () => {
		openHelp();
	};

	const handleOpenAssistant = () => {
		openAssistant();
	};

	useEffect(() => {
		const handleShortcut = (event: KeyboardEvent) => {
			if (
				(event.ctrlKey || event.metaKey) &&
				!event.shiftKey &&
				event.key.toLowerCase() === "k"
			) {
				event.preventDefault();
				handleOpenSearch();
			}
		};

		document.addEventListener("keydown", handleShortcut);

		return () => {
			document.removeEventListener("keydown", handleShortcut);
		};
	}, [sheets]);

	return (
		<div className="flex justify-between items-center h-12 bg-stone-100 px-6">
			<div className="flex-1 flex items-center h-full mt-2">
				<div id="header-left-content"></div>
			</div>
			{(!allDataInitialized || channelsLoading) && (
				<div className="flex items-center space-x-2 mx-4">
					<Loader2 className="h-4 w-4 animate-spin text-blue-600" />
					<span className="text-sm text-gray-600">
						{!allDataInitialized
							? "Stammdaten werden geladen..."
							: "Nachrichten werden geladen..."}
					</span>
				</div>
			)}
			<div className="flex items-center gap-3 mt-2">
				<div className="flex items-center gap-0.5">
					<button
						onClick={handleOpenAssistant}
						className="relative flex items-center justify-center w-8 h-8 text-gray-700 rounded-md hover:bg-stone-200 transition-colors"
						aria-label="AI Assistent öffnen"
						data-testid="ai-assistant-button"
					>
						<Sparkles className="h-4 w-4" />
					</button>
					<button
						onClick={handleOpenHelp}
						className="relative flex items-center justify-center w-8 h-8 text-gray-700 rounded-md hover:bg-stone-200 transition-colors"
						aria-label="Hilfe öffnen"
						data-testid="help-button"
					>
						<HelpCircle className="h-4 w-4" />
					</button>

					<ReleaseNotesButton />

					<div className="flex items-center ml-2">
						<PrivacySwitch />
					</div>
				</div>

				<button
					onClick={handleOpenSearch}
					className="flex items-center px-3 py-1 text-sm text-gray-700 bg-stone-100 rounded-md border border-gray-200 hover:bg-stone-200 transition-colors"
				>
					<Search className="h-3.5 w-3.5 pb-[1px]" />
					<span className="ml-1">Suche</span>
					<span className="text-xs px-1.5 bg-stone-100 rounded text-stone-500 ml-1">
						{/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent)
							? "⌘"
							: "STRG"}{" "}
						+ K
					</span>
				</button>
			</div>
		</div>
	);
};
